import { useAuthStore } from '@stores/auth';
import { useLegitimuzVerification } from '@/composables/base/useLegitimuzVerification';

interface ApiResponse {
  data: any;
  status?: number;
}

interface RefreshResponse {
  access_token: string;
  expires_in: number;
}

class ApiService {
  private static refreshingPromise: Promise<string | null> | null = null;
  private static isRefreshing = false;
  private static failedRefreshAttempts = 0;
  private static readonly MAX_REFRESH_ATTEMPTS = 3;
  private static readonly REFRESH_COOLDOWN = 1000;

  handleVPNDetection = async (authStore: any) => {
    const { isVPNDetected } = useLegitimuzVerification();
    isVPNDetected.value = true;
  };

  static async fetchAPI(
    method: string,
    path: string,
    body: any = null,
    headers: Record<string, string> = {}
  ): Promise<ApiResponse> {
    const authStore = useAuthStore();
    const { isVPNDetected } = useLegitimuzVerification();
    let authorizationToken = this.getAuthToken();

    if (authorizationToken) {
      const decodedToken = this.parseJwt(authorizationToken);
      const now = Date.now();

      const FIVE_MINUTES_MS = 5 * 60 * 1000;
      // const FIVE_MINUTES_MS = (3 * 60 * 60 * 1000) + (59 * 60 * 1000);
      const tokenExpirationMs = decodedToken.exp * 1000;
      const timeUntilExpiration = tokenExpirationMs - now;

      if (timeUntilExpiration <= FIVE_MINUTES_MS) {
        authorizationToken = await this.refreshToken();

        if (!authorizationToken) {
          console.error('Falha ao renovar o token após todas as tentativas.');
          authStore.setLoggedIn(false);
          window.location.href = '/login';
          return {
            data: { message: 'Token expirado ou inválido' },
            status: 401
          };
        }
      }
    }

    const baseHeaders = {
      Accept: 'application/json',
      Authorization: `Bearer ${authorizationToken}`,
      ...(authStore.state.userIp ? { 'User-Ip': authStore.state.userIp } : {}),
      ...useRequestHeaders(['x-forwarded-for']),
      'Cache-Control': 'private, max-age=600',
      ...headers
    };

    try {
      const response = await $fetch(path, {
        method,
        headers: baseHeaders,
        ...(body && { body })
      });

      // Check if the response indicates IP blocking
      if (response?.status === 'ip_blocked') {
        isVPNDetected.value = true;
        return {
          data: response,
          status: 403
        };
      }

      return {
        data: response,
        status: 200
      };
    } catch (error: any) {
      console.error('Erro na requisição:', error);
      const code = error.response?.status || 500;

      // Check for IP blocked in error response
      if (error.response?._data?.status === 'ip_blocked') {
        isVPNDetected.value = true;
        return {
          data: error.response._data,
          status: 403
        };
      }

      if ([401, 403].includes(code)) {
        const errorMessage = error.response?._data?.message;
        if (errorMessage === 'Unauthenticated.' && authStore.isLoggedIn) {
          const newToken = await this.refreshToken();
          if (newToken) {
            return this.fetchAPI(method, path, body, headers);
          }

          console.error('Usuário não autenticado após tentativa de refresh. Realizando logout...');
          authStore.setLoggedIn(false);

          useCookie('jwt').value = null;
          useCookie('balance').value = null;
          useCookie('user_id').value = null;
        }
      }

      return {
        data: error.response?._data || error,
        status: code
      };
    }
  }

  private static async waitForOngoingRefresh(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      const checkRefresh = async () => {
        if (!this.isRefreshing) {
          const token = this.getAuthToken();
          console.log(token);
          resolve(token);
        } else {
          setTimeout(checkRefresh, 100);
        }
      };
      checkRefresh();
    });
  }

  // Rest of the class methods (getAuthToken, refreshToken, parseJwt, etc.) remain the same
  static getAuthToken(): string | null {
    const tokenCookie = useCookie('jwt');
    const token = tokenCookie.value;

    if (token) {
      return token;
    }

    const headers = useRequestHeaders(['cookie']);
    if (headers.cookie) {
      const jwtMatch = headers.cookie.match(/jwt=([^;]*)/);
      return jwtMatch ? jwtMatch[1] : null;
    }

    return null;
  }

  static async refreshToken(): Promise<string | null> {
    if (this.isRefreshing) {
      return this.waitForOngoingRefresh();
    }

    if (this.failedRefreshAttempts >= this.MAX_REFRESH_ATTEMPTS) {
      const authStore = useAuthStore();
      authStore.setLoggedIn(false);
      console.error('Máximo de tentativas de renovação de token atingido');
      return null;
    }

    this.isRefreshing = true;
    const authStore = useAuthStore();
    const currentToken = this.getAuthToken();
    await authStore.getUserIp();

    if (!currentToken) {
      this.isRefreshing = false;
      return null;
    }

    try {
      const response = await $fetch<RefreshResponse>('/api/auth/refresh', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${currentToken}`,
          Accept: 'application/json'
        }
      });

      if (!response.access_token) {
        throw new Error('Resposta do refresh não contém um novo token');
      }

      this.failedRefreshAttempts = 0;
      const newToken = response.access_token;
      const jwtCookie = useCookie('jwt', {
        expires: new Date(Date.now() + response.expires_in * 1000),
        sameSite: 'none',
        secure: true
      });

      jwtCookie.value = newToken;
      this.isRefreshing = false;
      return newToken;
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      this.failedRefreshAttempts++;
      this.isRefreshing = false;

      await new Promise((resolve) => setTimeout(resolve, this.REFRESH_COOLDOWN));

      if (this.failedRefreshAttempts >= this.MAX_REFRESH_ATTEMPTS) {
        authStore.setLoggedIn(false);
        return null;
      }

      return this.refreshToken();
    }
  }

  static parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error("Erro ao decodificar o token JWT:", e);
      return null;
    }
  }

  static get(path: string, headers?: Record<string, string>): Promise<ApiResponse> {
    return this.fetchAPI("GET", path, null, headers);
  }

  static post(path: string, body: any, headers?: Record<string, string>): Promise<ApiResponse> {
    return this.fetchAPI("POST", path, body, headers);
  }

  static delete(path: string, headers?: Record<string, string>): Promise<ApiResponse> {
    return this.fetchAPI('DELETE', path, null, headers);
  }
}

export default ApiService;
